import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { MatDialog } from "@angular/material/dialog";
import { ToastrService } from "../../shared/toastr.service";
import { TranslateService } from "@ngx-translate/core";
import { SignaturesService } from "../signatures.service";
import { ActivatedRoute, Router } from "@angular/router";
import { UserService } from "../../users/users.service";
import { ConfirmationDialogComponent } from "../../shared/confirmation-dialog/confirmation-dialog.component";
import { AuthService } from "../../auth/auth.service";
import { Store } from "@ngrx/store";
import { SubmitUserComponent } from "../submit-user/submit-user.component";
import { StartCheckingForInstallationInProgressAction } from "../../signature-installation-state/signature-installation-state.actions";
import { SetForAllDialogComponent } from "./set-for-all-dialog/set-for-all-dialog.component";
import {
  SetForAll,
  Signature,
  InstallationResponse,
} from "../signatures.interface";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import DOMPurify from "dompurify";
import { FirstInstallationCongratulationsDialogComponent } from "../first-installation-congratulations-dialog/first-installation-congratulations-dialog.component";
import { getCurrentStateSelector } from "../../signature-installation-state/signature-installation-state.reducer";
import { SignatureInstallationState } from "../../signature-installation-state/signature-installation-state.interface";
import { Subject, forkJoin, of } from "rxjs";
import { catchError, finalize, switchMap, takeUntil } from "rxjs/operators";
import { GetIntegrationSettingsAction } from "../../settings/settings.actions";
import { getSettingsSelector } from "../../settings/settings.reducer";
import { IntegrationSettings } from "../../settings/settings.interface";
import { MatExpansionPanel } from "@angular/material/expansion";

@Component({
  selector: "app-signature-details",
  templateUrl: "./signature-details.component.html",
  styleUrls: ["./signature-details.component.scss"],
  standalone: false,
})
export class SignatureDetailsComponent implements OnInit, OnDestroy {
  installationMode: "ext" | "int" | "all" = "ext";
  isInternalSignatureEnabled = false;
  @ViewChild(SubmitUserComponent) submitUserComponent: SubmitUserComponent;
  footer: Signature;
  sanitizedContent: SafeHtml | null = null;
  preview = false;
  loading = true;
  updating = false;
  deleting = false;
  canDelete = false;
  domainRulesEnabled = false;
  private openDomainRulesFromQuery = false;
  private domainRulesNavigationHandled = false;
  private _domainRulesPanel?: MatExpansionPanel;
  @ViewChild("domainRulesPanel")
  set domainRulesPanel(panel: MatExpansionPanel | undefined) {
    this._domainRulesPanel = panel;
    this.tryOpenDomainRulesPanel();
  }
  private destroy$ = new Subject<void>();
  private installationStartTime: Date | null = null;

  get footerModifiedAt(): Date | undefined {
    return (this.footer as { modifiedAt?: Date })?.modifiedAt;
  }

  constructor(
    private service: SignaturesService,
    private auth: AuthService,
    private route: ActivatedRoute,
    private userService: UserService,
    private toastr: ToastrService,
    private router: Router,
    private store: Store,
    private translate: TranslateService,
    private dialog: MatDialog,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnInit(): void {
    this.store.dispatch(new GetIntegrationSettingsAction());
    this.store
      .select(getSettingsSelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((settings: IntegrationSettings | null) => {
        this.domainRulesEnabled = settings?.domainRulesEnabled === true;
        this.tryOpenDomainRulesPanel();
      });

    const templateId = this.route.snapshot.params["templateId"] as string;
    this.loading = true;
    this.service
      .byId(templateId)
      .pipe(
        switchMap((f: Signature) =>
          forkJoin({
            signature: of(f),
            previewHtml: this.service
              .preview(f as unknown as Record<string, unknown>)
              .pipe(catchError(() => of(""))),
          }),
        ),
        finalize(() => {
          this.loading = false;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: ({ signature: f, previewHtml }) => {
          this.footer = f;
          this.isInternalSignatureEnabled = !!(
            f as { internalSignature?: boolean }
          ).internalSignature;
          this.canDelete = !(
            f.numberOfInstallations > 0 || f.numberOfAliases > 0
          );
          this.sanitizedContent = this.sanitizer.bypassSecurityTrustHtml(
            DOMPurify.sanitize(previewHtml ?? "", {
              ALLOWED_TAGS: [
                "a",
                "b",
                "br",
                "em",
                "i",
                "img",
                "p",
                "span",
                "strong",
                "table",
                "td",
                "tr",
                "div",
                "ul",
                "ol",
                "li",
              ],
            }),
          );
          this.preview = true;
          this.tryOpenDomainRulesPanel();
        },
      });

    this.store
      .select(getCurrentStateSelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((state: SignatureInstallationState | null) => {
        if (state && this.installationStartTime) {
          this.handleInstallationCompletion(state);
        }
      });

    this.route.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        this.openDomainRulesFromQuery = params.get("domainRules") === "true";
        this.domainRulesNavigationHandled = !this.openDomainRulesFromQuery;
        this.tryOpenDomainRulesPanel();
      });
  }

  refreshFooter(): void {
    const templateId = this.route.snapshot.params["templateId"] as string;
    void this.service
      .byId(templateId)
      .toPromise()
      .then((f: Signature) => {
        this.footer = f;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onRadioChange(mode: "ext" | "int" | "all"): void {
    if (!this.preview) {
      return;
    }

    if (
      !this.isInternalSignatureEnabled &&
      (mode === "int" || mode === "all")
    ) {
      this.installationMode = mode;
      this.toastr.warning(
        this.translate.instant("footers.installMode") as string,
      );
      setTimeout(() => {
        this.triggerSelectorBounceAnimation();
        this.installationMode = "ext";
      }, 200);
    } else {
      this.installationMode = mode;
    }
  }

  private triggerSelectorBounceAnimation(): void {
    const el = (this as { selectorEl?: { nativeElement: HTMLElement } })
      .selectorEl?.nativeElement;
    if (!el) {
      return;
    }
    el.classList.add("bounce-back");
    const remove = () => {
      el.classList.remove("bounce-back");
      el.removeEventListener("animationend", remove);
    };
    el.addEventListener("animationend", remove);
  }

  updateInstallationStatus(): void {
    void this.service
      .byId(this.footer.templateId)
      .toPromise()
      .then((f: Signature) => {
        this.footer.numberOfInstallations = f.numberOfInstallations;
        this.footer.numberOfAliases = f.numberOfAliases;
        this.canDelete = !(
          f.numberOfInstallations > 0 || f.numberOfAliases > 0
        );
      });
  }

  setForAll() {
    const dialogRef = this.dialog.open(SetForAllDialogComponent, {
      panelClass: "app-dialog-panel",
      data: { selectedOption: SetForAll.EMPLOYEES },
    });
    dialogRef.afterClosed().subscribe((res: string | undefined) => {
      if (res) {
        this.assign({ all: res });
      }
    });
  }

  setForUser(value: Record<string, unknown>) {
    this.assign({ ...value });
  }
  assignToGroup(group?: string) {
    this.assign({ group });
  }
  assignToGroupWithAlias(g: Record<string, unknown>) {
    this.assign(g);
  }
  assignToUnit(unit?: string) {
    this.assign({ unit });
  }
  assignToBranch(branchId?: string) {
    this.assign({ branch: branchId });
  }

  private assign(
    body: Record<string, unknown>,
    templateId: string | null = this.footer?.templateId?.trim() ||
      (
        this.route.snapshot.params["templateId"] as string | undefined
      )?.trim() ||
      null,
  ) {
    this.installationStartTime = new Date();
    this.store.dispatch(new StartCheckingForInstallationInProgressAction());
    this.updating = true;

    void this.userService
      .updateAll({
        ...body,
        installMode: this.installationMode,
        templateId,
      })
      .toPromise()
      .then((resp: InstallationResponse) => {
        if (resp.updated === 0 && resp.limit) {
          const reason = resp.reason as "freemium" | "yearly" | undefined;
          if (reason === "freemium") {
            this.toastr.info(
              this.translate.instant("footers.billingLimitExceededFreemium", {
                limit: resp.limit,
              }) as string,
            );
          } else if (reason === "yearly") {
            this.toastr.info(
              this.translate.instant("footers.billingLimitExceededYearly", {
                limit: resp.limit,
              }) as string,
            );
          } else {
            this.toastr.info(
              this.translate.instant("footers.billingLimitExceeded", {
                limit: resp.limit,
              }) as string,
            );
          }
          this.installationStartTime = null;
        } else {
          if (resp.isFirstInstallation) {
            this.dialog.open(FirstInstallationCongratulationsDialogComponent, {
              width: "500px",
              disableClose: true,
              panelClass: "app-dialog-panel",
            });
          }
        }
        this.submitUserComponent?.clearSelection();
        this.updateInstallationStatus();
      })
      .catch((error: HttpErrorResponse) => {
        this.installationStartTime = null;
        if (error.status > 0 && error.status < 500) {
          const body = error.error as { message?: string } | string | null;
          const message =
            typeof body === "object" && body && typeof body.message === "string"
              ? body.message
              : typeof body === "string"
                ? body
                : null;
          this.toastr.error(
            message || (this.translate.instant("error") as string),
          );
        }
      })
      .finally(() => (this.updating = false));
  }

  uninstallAllSignatures(group: string) {
    this.assign({ group }, null);
  }

  delete() {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: this.translate.instant(
            "footers.deleteConfirmationText",
          ) as string,
        },
      })
      .afterClosed()
      .subscribe((confirm) => {
        if (!confirm) {
          return;
        }
        this.deleting = true;
        const templateId = this.route.snapshot.params["templateId"] as string;
        void this.service
          .delete(templateId)
          .toPromise()
          .then(() =>
            this.toastr.success(
              this.translate.instant("footers.deleted") as string,
            ),
          )
          .then(() => this.goToSignatures())
          .finally(() => (this.deleting = false));
      });
  }

  setForMe() {
    this.assign({ email: this.auth.appUser?.email || "" });
  }

  goToSignatures() {
    void this.router.navigate(["signatures"]);
  }

  private handleInstallationCompletion(
    state: SignatureInstallationState,
  ): void {
    if (!this.installationStartTime || !state.modifiedAt) {
      return;
    }

    const stateModifiedTime = new Date(state.modifiedAt);
    if (stateModifiedTime < this.installationStartTime) {
      return;
    }

    if (
      state.status === "COMPLETED_SUCCESSFULLY" ||
      state.status === "COMPLETED_WITH_ERROR"
    ) {
      const successfulInstallations = state.installations.filter(
        (i) => !i.error,
      );
      const totalSuccessful = successfulInstallations.length;

      if (state.status === "COMPLETED_WITH_ERROR" && totalSuccessful === 0) {
        this.toastr.info(
          this.translate.instant(
            "installation.noInstallationsSuccess",
          ) as string,
        );
      } else {
        this.toastr.success(
          this.translate.instant("footers.assigned") as string,
        );
      }

      this.installationStartTime = null;
    }
  }

  private tryOpenDomainRulesPanel(): void {
    if (
      !this.openDomainRulesFromQuery ||
      !this.domainRulesEnabled ||
      !this._domainRulesPanel ||
      this.domainRulesNavigationHandled
    ) {
      return;
    }
    this._domainRulesPanel.open();
    this.domainRulesNavigationHandled = true;
    setTimeout(() => {
      const element = document.getElementById("domain-rules");
      element?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  }
}
